import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import {
  GeneralUserInfo,
  BaseUserInformation,
  BaseInfoFormValues,
} from '../../types';
import { CustomerProps } from '../../../common/types';
import { individualCustomersApi } from '../services/individual';
import { corporateCustomersApi } from '../../../corporate/store/services/corporate';
import { customersComponentApi } from '../../../customers/store/services/customers';
import { LinkedClient } from '../../../common/linked-clients/types';
import { CofProps } from '../../../corporate/types';

import ROUTES from '../../../../routes';

export interface IndividualCustomerOverviewState {
  generalUserInfo: {
    isLoading: boolean;
    values?: GeneralUserInfo;
    formValues?: BaseInfoFormValues;
  };
  baseUserInformation?: Partial<GeneralUserInfo> & BaseUserInformation;
}

const initialState: IndividualCustomerOverviewState = {
  generalUserInfo: {
    isLoading: false,
    formValues: undefined,
  },
};

export const individualCustomerOverviewSlice = createSlice({
  name: 'individualCustomerOverview',
  initialState,
  reducers: {
    clearOverviewState: (state) => {
      state.baseUserInformation = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      individualCustomersApi.endpoints.getIndividualUser.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerProps }>) => {
        const { data } = action.payload;

        state.baseUserInformation = {
          fullName: `${data.firstName} ${data.lastName}`,
          firstName: data.firstName,
          lastName: data.lastName,
          address1: data.address1,
          address2: data.address2,
          city: data.city,
          postalCode: data.postalCode,
          employmentStatus: data.employmentStatus,
          residenceAddress: `${data.address1},${
            data.address2 ? ` ${data.address2},` : ''
          } ${data.city} ${data.postalCode}`,
          countryId: data.countryId,
          sourceOfFunds: data.sourcesOfFunds.edges.map((item) => item.node.id),
          products: data?.products?.edges?.map((item) => item.node.id),
          dateOfBirth: data?.user?.profile?.dob,
          phone: data?.user?.phone,
          email: data?.user?.profile?.additionalEmail || data?.user?.email,
          type: data.type,
          clientActivityId: data.clientActivityId,
          pspIntegrationOrganization: data?.pspIntegrationOrganization,
          representativeOfOrganizations:
            data.representativeOfOrganizations.map((item) => ({
              ...item,
              url: generatePath(
                ROUTES.CUSTOMERS_CORPORATE_OVERVIEW_TRANSACTIONS,
                {
                  customerId: item.organizationId,
                },
              ),
              accessRight: item.accessRight,
            })) || [],
        };
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.bindExternalOrganization.matchFulfilled,
      (state, action: PayloadAction<{ data: LinkedClient }>) => {
        if (state.baseUserInformation) {
          state.baseUserInformation = {
            ...state.baseUserInformation,
            pspIntegrationOrganization: action.payload.data,
          };
        }
      },
    );
    builder.addMatcher(
      individualCustomersApi.endpoints.updateIndividualUserBaseInformation
        .matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerProps }> & any) => {
        const { data } = action.payload;

        state.baseUserInformation = {
          ...state.baseUserInformation,
          fullName: `${data.firstName} ${data.lastName}`,
          firstName: data.firstName,
          lastName: data.lastName,
          address1: data.address1,
          address2: data.address2,
          city: data.city,
          postalCode: data.postalCode,
          clientActivityId: data.clientActivityId,
          employmentStatus: data.employmentStatus,
          residenceAddress: `${data.address1},${
            data.address2 ? ` ${data.address2},` : ''
          } ${data.city} ${data.postalCode}`,
          countryId: data.countryId,
          sourceOfFunds: data.sourcesOfFunds.edges.map(
            (item: { node: { id: string } }) => item.node.id,
          ),
          products: data?.products?.edges?.map(
            (item: { node: { id: string } }) => item.node.id,
          ),
          dateOfBirth:
            action?.meta?.arg?.originalArgs?.body?.dob ||
            data?.user?.profile?.dob,
          phone: data?.user?.phone,
          email:
            action?.meta?.arg?.originalArgs?.body?.email || data?.user?.email,
          type: data.type,
          idType: data.idType,
          idExpDate: data.idExpDate,
          idNumber: data.idNumber,
        };
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.updateCofStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: CofProps }>) => {
        if (state.baseUserInformation) {
          const nextCompanyOfficial =
            state.baseUserInformation.representativeOfOrganizations?.map(
              (item) => {
                let result = { ...item };

                if (action.payload.data.id === result.id) {
                  result = {
                    ...result,
                    status: action.payload.data.status,
                    accessRight: action.payload.data.accessRight,
                    rejectedAt: action.payload.data.rejectedAt,
                    rejectionReason: action.payload.data.rejectionReason,
                    archivedAt: action.payload.data.archivedAt,
                    archivedBy: action.payload.data.archivedBy,
                    rejectedBy: action.payload.data.rejectedBy,
                    approvedBy: action.payload.data.approvedBy,
                    approvedAt: action.payload.data.approvedAt,
                  };
                }

                return result;
              },
            );

          const nextBaseInformation = { ...state.baseUserInformation };

          nextBaseInformation.representativeOfOrganizations =
            nextCompanyOfficial;

          state.baseUserInformation = nextBaseInformation;
        }
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.updateCorporateCOF.matchFulfilled,
      (state, action: PayloadAction<{ data: CofProps }>) => {
        if (state.baseUserInformation) {
          const nextCompanyOfficial =
            state.baseUserInformation.representativeOfOrganizations?.map(
              (item) => {
                let result = { ...item };

                if (action.payload.data.id === result.id) {
                  result = {
                    ...result,
                    position: action.payload.data.position,
                    percentage: action.payload.data.percentage,
                    natureOfControl: action.payload.data.natureOfControl,
                    signatureType: action.payload.data.signatureType,
                    accessRight: action.payload.data.accessRight,
                  };
                }

                return result;
              },
            );

          const nextBaseInformation = { ...state.baseUserInformation };

          nextBaseInformation.representativeOfOrganizations =
            nextCompanyOfficial;

          state.baseUserInformation = nextBaseInformation;
        }
      },
    );
  },
});

export const { clearOverviewState } = individualCustomerOverviewSlice.actions;

export default individualCustomerOverviewSlice.reducer;
